<div class="bcg p-4">
  <app-bulk-upload-estamp-step [currentStep]="2"></app-bulk-upload-estamp-step>

  <!-- Step 2 - Upload PDF Documents Section -->
  <div class="upload-section mt-4">
    <div class="row align-items-center mb-4">
      <div class="col-sm-12">
        <h5 class="mb-0">Step 2 - Upload PDF Documents</h5>
        <p class="text-muted small mb-0 mt-2">
          Select PDF files one at a time or in batches — files from different
          folders are supported. Files must match exactly with the names
          specified in Excel.
        </p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center gap-2">
        <input
          type="file"
          #fileInput
          (change)="onFileSelect($event)"
          accept=".pdf"
          multiple
          style="display: none"
        />
        <button
          class="btn back-excel me-2"
          (click)="fileInput.click()"
          [disabled]="isValidatingFiles"
        >
          <i class="pi pi-upload me-2"></i>
          <span>Add PDF Files</span>
        </button>
        <span
          class="badge"
          [class.bg-success]="collectedCount === expectedFileNames.length"
          [class.bg-warning]="collectedCount < expectedFileNames.length"
          style="font-size: 13px; padding: 6px 12px"
        >
          {{ collectedCount }} / {{ expectedFileNames.length }} files added
        </span>
      </div>
      <button
        class="btn back-excel"
        (click)="startUpload()"
        [disabled]="!isFilesSelected || isValidatingFiles"
        [class.btn-disabled]="!isFilesSelected || isValidatingFiles"
      >
        <i class="pi pi-cloud-upload me-2"></i>
        <span>Start Upload</span>
      </button>
    </div>

    <div class="card mb-3" *ngIf="isValidatingFiles">
      <div class="card-body">
        <div class="text-center py-4">
          <i
            class="pi pi-spin pi-spinner"
            style="font-size: 2rem; color: #6366f1"
          ></i>
          <p class="mt-3 mb-0">Validating selected files...</p>
          <small class="text-muted"
            >Checking file names, types, sizes, and page counts...</small
          >
        </div>
      </div>
    </div>


    <div class="card mb-4">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 class="mb-0">
            <i class="pi pi-list me-2"></i>
            Files ({{ collectedCount }} / {{ expectedFileNames.length }} added)
          </h6>
          <span class="text-muted" style="font-size: 12px">{{ collectedPercent }}% complete</span>
        </div>
        <p-progressBar
          [value]="collectedPercent"
          [showValue]="false"
          styleClass="files-progress mt-2"
        ></p-progressBar>
      </div>

      <div class="card-body pb-0">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div class="d-flex flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-sm file-filter-chip"
              [class.active]="fileFilterMode === 'all'"
              (click)="setFileFilter('all')"
            >
              All <span class="badge bg-light text-dark ms-1">{{ expectedFileNames.length }}</span>
            </button>
            <button
              type="button"
              class="btn btn-sm file-filter-chip"
              [class.active]="fileFilterMode === 'missing'"
              (click)="setFileFilter('missing')"
            >
              Missing <span class="badge bg-light text-dark ms-1">{{ missingFileNames.length }}</span>
            </button>
            <button
              type="button"
              class="btn btn-sm file-filter-chip"
              [class.active]="fileFilterMode === 'added'"
              (click)="setFileFilter('added')"
            >
              Added <span class="badge bg-light text-dark ms-1">{{ collectedCount }}</span>
            </button>
            <button
              *ngIf="issuesFileNames.length > 0"
              type="button"
              class="btn btn-sm file-filter-chip"
              [class.active]="fileFilterMode === 'issues'"
              (click)="setFileFilter('issues')"
            >
              Issues <span class="badge bg-light text-dark ms-1">{{ issuesFileNames.length }}</span>
            </button>
          </div>
          <div style="min-width: 220px">
            <app-search-input
              [(ngModel)]="fileSearchText"
              (ngModelChange)="onFileSearchChange($event)"
              placeholder="Search file name..."
            ></app-search-input>
          </div>
        </div>
      </div>

      <div class="card-body p-0">
        <app-data-table
          [columns]="fileColumns"
          [data]="pagedFileNames"
          [totalRecords]="filteredFileNames.length"
          [rows]="pageSize"
          [rowsPerPageOptions]="[50, 100, 200]"
          [paginator]="filteredFileNames.length > pageSize"
          emptyMessage="No files match this filter."
          [getRowClass]="fileRowClass"
          (pageChange)="onFileListPageChange($event)">
          <ng-template appTableCell="s_no" let-row let-i="index">
            <span class="text-muted" style="font-size: 12px">
              {{ pageFirstIndex + i + 1 }}
            </span>
          </ng-template>

          <ng-template appTableCell="file_name" let-row>
            <div class="d-flex flex-column">
              <div class="d-flex align-items-center gap-2">
                <i
                  class="pi pi-file-pdf"
                  [style.color]="
                    getFileStatus(row) === 'valid'
                      ? '#198754'
                      : getFileStatus(row) === 'invalid'
                        ? '#dc3545'
                        : '#6c757d'
                  "
                ></i>
                <span style="font-size: 13px; word-break: break-all">{{
                  row
                }}</span>
              </div>
              <small
                *ngIf="getFileError(row)"
                class="text-danger mt-1"
                style="font-size: 11px; padding-left: 20px"
              >
                <i class="pi pi-exclamation-circle me-1"></i>
                {{ getFileError(row) }}
              </small>
            </div>
          </ng-template>
        </app-data-table>
      </div>
    </div>

    <div
      class="alert alert-warning d-flex align-items-start gap-2 mb-3"
      *ngIf="skippedSignedFiles.length > 0 && !isValidatingFiles"
    >
      <i class="pi pi-exclamation-triangle mt-1" style="flex-shrink: 0"></i>
      <div>
        <strong>
          {{ skippedSignedFiles.length }} file(s) skipped — already signed:
        </strong>
        <ul class="mb-0 mt-1" style="font-size: 13px">
          <li *ngFor="let name of skippedSignedFiles">{{ name }}</li>
        </ul>
        <small class="text-muted mt-1 d-block">
          These files already contain a signature and have been automatically
          removed. Your order will proceed with the remaining
          {{ expectedFileNames.length }} file(s).
        </small>
      </div>
    </div>

    <div
      class="alert alert-danger d-flex align-items-start gap-2 mb-3"
      *ngIf="unexpectedFiles.length > 0 && !isValidatingFiles"
    >
      <i class="pi pi-times-circle mt-1" style="flex-shrink: 0"></i>
      <div class="flex-grow-1">
        <div class="unexpected-files-header">
          <strong>{{ unexpectedFiles.length }} file(s) not found in Excel:</strong>
          <button
            type="button"
            class="btn-remove-all"
            (click)="removeAllUnexpectedFiles()"
            [disabled]="isValidatingFiles"
            title="Remove all files not found in Excel"
          >
            <i class="pi pi-trash"></i> Remove All
          </button>
        </div>
        <ul class="mb-0 mt-1" style="font-size: 13px">
          <li *ngFor="let file of unexpectedFiles" class="mb-1">
            <span>{{ file.name }}</span>
          </li>
        </ul>
        <small class="text-muted mt-1 d-block">
          These files have no matching record in the Excel. Remove them here, or update your Excel to include these entries and re-upload.
        </small>
      </div>
    </div>

    <div class="card mb-3" *ngIf="isFilesSelected && !isValidatingFiles">
      <div class="card-body">
        <div class="alert alert-success mb-0">
          <div class="d-flex align-items-center">
            <i class="pi pi-check-circle me-3" style="font-size: 24px"></i>
            <div class="flex-grow-1">
              <h6 class="alert-heading mb-1">
                <strong>All Files Validated Successfully!</strong>
              </h6>
              <p class="mb-0">
                {{ expectedFileNames.length }} files are ready to upload. Click
                "Start Upload" to begin.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
<p-toast></p-toast>